import React, { useState, useEffect } from 'react';
import { Box, CssBaseline, useMediaQuery, useTheme } from '@mui/material';
import { Outlet } from 'react-router-dom';
import TopBar from '@/widgets/TopBar';
import Sidebar from '@/widgets/Sidebar';
import { useToast } from '@/shared/ui';
import { useAuth } from '@/features/auth';
import { useMercadoPagoAutoCheck, usePaymentSetupFlow } from '@/shared/hooks';

const drawerWidth = 240;
const collapsedDrawerWidth = 72;

const AppShell: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // Set to 'sm' (600px)
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const { logout } = useAuth();
  const { success } = useToast();
  
  // Automatically check MercadoPago status when user logs in
  useMercadoPagoAutoCheck();

  // Handle payment setup flow - show payment setup page once after login/register
  usePaymentSetupFlow();

  useEffect(() => {
    // Close drawer when switching to desktop view
    if (!isMobile && mobileOpen) {
      setMobileOpen(false);
    }
  }, [isMobile, mobileOpen]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };
  
  const handleCollapseToggle = () => {
    setIsCollapsed(!isCollapsed);
  };

  const handleLogout = () => {
    // Use the logout function from auth context
    logout();
    
    // Show success message
    success('Sesión cerrada exitosamente');
  };

  const currentDrawerWidth = isCollapsed ? collapsedDrawerWidth : drawerWidth;
  
  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <CssBaseline />
      
      <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
        {/* TopBar Component - spans full width */}
        <TopBar 
          drawerWidth={isMobile ? 0 : currentDrawerWidth} 
          handleDrawerToggle={handleDrawerToggle} 
          onLogout={handleLogout}
        />
        
        <Box sx={{ 
          display: 'flex',
          mt: { xs: 7, sm: 8 },
          flexGrow: 1
        }}>
          {/* Sidebar Component */}
          <Box 
            sx={{ 
              display: 'flex',
              width: { xs: 0, sm: currentDrawerWidth }, 
              transition: theme => theme.transitions.create('width') 
            }}
          >
            <Sidebar 
              drawerWidth={drawerWidth}
              collapsedWidth={collapsedDrawerWidth}
              isCollapsed={isCollapsed} 
              mobileOpen={mobileOpen} 
              handleDrawerToggle={handleDrawerToggle}
              handleCollapseToggle={handleCollapseToggle}
            />
          </Box>
          
          {/* Main Content */}
          <Box
            component="main"
            sx={{ 
              flexGrow: 1,
              p: 3, 
              backgroundColor: theme => theme.palette.mode === 'light' 
                ? theme.palette.grey[100] 
                : theme.palette.background.default
            }}
          >
            <Box sx={{ py: 2 }} data-testid="main-content">
              <Outlet /> {/* This is where the nested routes render */}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default AppShell;
